import { Injectable } from '@angular/core';
import firebase from "firebase/app";
import 'firebase/remote-config';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AngularFireAnalytics } from "@angular/fire/analytics";
import { Dictionary } from 'src/app/utils/custom-types/custom-types';

export enum mobileOS {
  ANDROID = 'android',
  IOS = 'ios',
}

@Injectable({
  providedIn: 'root'
})
export class TranslateFirebaseService {
  
  private remoteConfig = firebase.remoteConfig();
  private osStoreStringBase: string = 'webapp_qr_code_';

  public translations : any;
  private setup : boolean = false;
  
  constructor(private httpClient : HttpClient,
    public router : Router, private analytics: AngularFireAnalytics) {
    if(!this.setup){
      this.setupTranslations();
    }
  }
  
  private setupTranslations() {
    const languageId = window.navigator.language  || 'en';

    if(environment.production) {
      //this.remoteConfig.settings.minimumFetchIntervalMillis = 43200000;
      this.remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
      this.analytics.setUserProperties({ language: languageId });
    } else {
      this.analytics.setUserProperties({ language: languageId, environment: "QA" });
      this.remoteConfig.settings.minimumFetchIntervalMillis = 60000;
    }
    this.remoteConfig.fetchAndActivate().then(res => {
      const remoteConfigTranslations = this.remoteConfig.getAll();
      const remoteTranslations = this.convertRemoteConfigToDictionary(remoteConfigTranslations);
      this.httpClient.get<Dictionary>("assets/i18n/en.json").subscribe(localTranslations => {
        const importedKeys = new Set<string>();
        this.translations = this.mergeDictionaries(remoteTranslations, localTranslations, importedKeys);
        console.log("Chiavi da tradurre su Firebase:", Array.from(importedKeys));
        this.setup = true;
      },
      error => {
        console.error("An error occurred during local translation loads", error);
        this.translations = remoteTranslations;
        this.setup = true;
      });
    }, error => {
      this.defaultTranslations();
    });
  }
  
  defaultTranslations() {
    this.httpClient.get<Dictionary>("assets/i18n/en.json")
    .subscribe(result => {
      this.translations = result;
    });
  }

  getTranslation(key: string): string {
    if (this.translations == undefined) return;
    else if (!this.setup) return this.translations[key];
    else if (this.translations[key]) return this.translations[key];
    else return "";
  }
  
  /**
   * Determine the mobile operating system.
   * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
  *
  * @returns {String}
  */
 getMobileOperatingSystem(): string {
   var userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
   
   // Windows Phone must come first because its UA also contains "Android"
   if (/windows phone/i.test(userAgent)) {
     return "Windows Phone";
    }
    
    if (/android/i.test(userAgent)) {
      return mobileOS.ANDROID;
    }
    
    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return mobileOS.IOS;
    }
    
    return "default";
  }

// Add this property at the class level
private isLoading: boolean = false;

async redirectToStore(): Promise<void> {
  this.isLoading = true;
  
  try {
    while (this.isLoading) {
         var userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

      const res = await this.remoteConfig.fetchAndActivate();
      this.translations = this.remoteConfig.getAll();
      const storeUrlKey = this.osStoreStringBase + this.getMobileOperatingSystem();
      const storeUrlValue = this.translations[storeUrlKey];
      // Estrai il valore _value se è un oggetto Firebase Remote Config
      const storeUrl = storeUrlValue?._value || storeUrlValue;
      
      if (storeUrl) {
        this.isLoading = false;
        window.location.href = storeUrl;
      
      } else {
        this.isLoading = false;
        
        this.router.navigateByUrl('/prelogin');
        
      }

      if (this.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  } catch (error) {
    console.error('Error in redirectToStore:', error);
    this.isLoading = false;
    this.defaultTranslations();
  }
}
  private mergeDictionaries(dict1: Dictionary, dict2: Dictionary, importedKeys: Set<string> = new Set(), path: string = ''): Dictionary {
    for (const key in dict2) {
      const fullPath = path ? `${path}.${key}` : key; // Per tracciare il percorso della chiave
  
      if (!(key in dict1)) {
        // Se la chiave non esiste in dict1, la aggiungiamo
        dict1[key] = dict2[key];
        importedKeys.add(fullPath);
      } else if (typeof dict1[key] === 'object' && typeof dict2[key] === 'object' && !Array.isArray(dict1[key]) && !Array.isArray(dict2[key])) {
        // Se entrambi i valori sono sotto-dizionari, facciamo il merge ricorsivo
        this.mergeDictionaries(dict1[key] as Dictionary, dict2[key] as Dictionary, importedKeys, fullPath);
      }
    }
    return dict1;
  }

  private convertRemoteConfigToDictionary(config: { [key: string]: any }): Dictionary {
    const extracted: Dictionary = {};
  
    Object.keys(config).forEach(key => {
      const value = config[key];
  
      try {
        if (value.getSource() === 'static') return; // Ignora le chiavi predefinite di Firebase Remote Config
  
        const jsonValue = value.asJson();
        if (typeof jsonValue === "object" && jsonValue !== null) {
          extracted[key] = jsonValue as Dictionary; // Se è un oggetto JSON, lo conserva come sotto-dizionario
        } else {
          extracted[key] = value.asString(); // Altrimenti lo tratta come stringa
        }
      } catch (error) {
        // console.warn(`Errore nella conversione della chiave ${key}:`, error);
        extracted[key] = value.asString(); // Fallback a stringa se non è convertibile
      }
    });
  
    return extracted;
  }
}